import React, { useEffect, useMemo, useState } from 'react';
import { bankAccountsApi } from '../../../../api/bankApi';
import {
  formatOrdersCountLabel,
  isBankAccountReadyForInvoice,
} from '../../../../utils/bankAccountInvoiceUtils';

/**
 * Модалка перед формуванням рахунку: вибір банківського рахунку.
 * Поле «Покупець» — лише коли замовлень більше одного.
 */
const InvoiceBuyerModal = ({
  show,
  onClose,
  onConfirm,
  loading = false,
  ordersCount = 0,
  initialBuyer = '',
  exportError = '',
}) => {
  const needBuyerInput = Number(ordersCount) > 1;
  const [buyer, setBuyer] = useState(initialBuyer);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState('');
  const [error, setError] = useState('');

  const invoiceAccounts = useMemo(
    () => (accounts || []).filter(isBankAccountReadyForInvoice),
    [accounts]
  );

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setBuyer(initialBuyer || '');
    setError('');
    setAccountsError('');
    setSelectedAccountId('');
    setAccountsLoading(true);

    bankAccountsApi.getAll()
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.data;
        const next = Array.isArray(list) ? list : [];
        setAccounts(next);
        const ready = next.filter(isBankAccountReadyForInvoice);
        if (ready.length === 1) {
          setSelectedAccountId(String(ready[0].id));
        }
      })
      .catch(() => {
        if (cancelled) return;
        setAccounts([]);
        setAccountsError('Не вдалося завантажити банківські рахунки');
      })
      .finally(() => {
        if (!cancelled) setAccountsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, initialBuyer]);

  useEffect(() => {
    setError(exportError || '');
  }, [exportError]);

  if (!show) return null;

  const clearLocalError = () => {
    if (error) setError('');
  };

  const handleConfirm = () => {
    const account = invoiceAccounts.find((a) => String(a.id) === String(selectedAccountId));
    if (!account) {
      setError(invoiceAccounts.length
        ? 'Оберіть банківський рахунок'
        : 'Немає рахунків із заповненими реквізитами — додайте їх у «Банківські рахунки»');
      return;
    }

    if (needBuyerInput) {
      const trimmed = (buyer || '').trim();
      if (!trimmed) {
        setError('Вкажіть покупця для рахунку');
        return;
      }
      setError('');
      onConfirm({ buyer: trimmed, bankAccount: account });
      return;
    }

    setError('');
    onConfirm({ buyer: (initialBuyer || '').trim() || null, bankAccount: account });
  };

  const displayError = error || exportError;
  const canClose = !loading;

  return (
    <>
      <div className="modal-backdrop show" style={{ zIndex: 1040 }} />
      <div
        className="modal show"
        tabIndex="-1"
        role="dialog"
        style={{ display: 'block', zIndex: 1050 }}
        onClick={() => canClose && onClose()}
      >
        <div className="modal-dialog modal-dialog-centered" role="document" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header align-items-center py-2">
              <div>
                <h5 className="modal-title mb-0">Рахунок на оплату</h5>
                <div className="text-muted" style={{ fontSize: '0.8rem', lineHeight: 1.2 }}>
                  {formatOrdersCountLabel(ordersCount)} · без ПДВ
                </div>
              </div>
              <button type="button" className="close" aria-label="Close" onClick={() => canClose && onClose()}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body text-left">
              <label htmlFor="invoiceBankAccountSelect" className="font-weight-bold mb-1 d-block">
                Банківський рахунок
              </label>
              {accountsLoading ? (
                <div className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>Завантаження рахунків…</div>
              ) : (
                <select
                  id="invoiceBankAccountSelect"
                  className="form-control mb-1"
                  value={selectedAccountId}
                  disabled={loading || !invoiceAccounts.length}
                  onChange={(e) => {
                    setSelectedAccountId(e.target.value);
                    clearLocalError();
                  }}
                >
                  <option value="">
                    {invoiceAccounts.length ? 'Оберіть рахунок…' : 'Немає рахунків з реквізитами'}
                  </option>
                  {invoiceAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                      {account.recipientName ? ` — ${account.recipientName}` : ''}
                    </option>
                  ))}
                </select>
              )}
              <div className="form-text text-muted mb-3" style={{ fontSize: '0.8rem' }}>
                Реквізити беруться з обраного рахунку (отримувач, ЄДРПОУ, IBAN, банк).
                {!accountsLoading && !invoiceAccounts.length && (
                  <> Заповніть їх у розділі «Банківські рахунки».</>
                )}
              </div>
              {accountsError && (
                <div className="alert alert-warning py-2" role="alert">{accountsError}</div>
              )}

              {needBuyerInput && (
                <>
                  <label htmlFor="invoiceBuyerInput" className="font-weight-bold mb-1 d-block">
                    Покупець
                  </label>
                  <textarea
                    id="invoiceBuyerInput"
                    className="form-control"
                    rows={3}
                    value={buyer}
                    disabled={loading}
                    placeholder={"Прізвище Ім'я, телефон, місто, відділення"}
                    onChange={(e) => {
                      setBuyer(e.target.value);
                      clearLocalError();
                    }}
                  />
                  <div className="form-text text-muted mt-1" style={{ fontSize: '0.8rem' }}>
                    Для кількох замовлень вкажіть покупця вручну — він підставиться в рахунок.
                  </div>
                </>
              )}

              {displayError && (
                <div className="alert alert-danger mt-3 mb-0" role="alert">
                  {displayError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                Скасувати
              </button>
              <button type="button" className="btn btn-dark" onClick={handleConfirm} disabled={loading || accountsLoading}>
                {loading ? 'Формування…' : 'Сформувати рахунок'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceBuyerModal;
